import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import PageTitle from '../shared/PageTitle';

const API_URL = process.env.REACT_APP_API_URL || 'https://nivaas.app';

const Testimonials = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [formData, setFormData] = useState({
        author_name: '',
        author_title: '',
        author_company: '',
        rating: 5,
        testimonial_text: '',
        is_active: false, // Default to inactive for new testimonials
        display_order: 0
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [removeImage, setRemoveImage] = useState(false);

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const fetchTestimonials = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/api/testimonials`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setTestimonials(response.data.testimonials || []);
            setStats(response.data.stats || {});
        } catch (error) {
            console.error('Error fetching testimonials:', error);
            toast.error('Failed to fetch testimonials');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            if (!allowedTypes.includes(file.type)) {
                toast.error('Only JPG, JPEG, and PNG files are allowed');
                return;
            }

            // Validate file size (5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB
            if (file.size > maxSize) {
                toast.error('File size must be less than 5MB');
                return;
            }

            setSelectedFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const formDataToSend = new FormData();

            // Add form fields
            Object.keys(formData).forEach(key => {
                if (key === 'is_active') {
                    formDataToSend.append(key, formData[key] ? '1' : '0');
                } else {
                    formDataToSend.append(key, formData[key]);
                }
            });

            // Add file if selected
            if (selectedFile) {
                formDataToSend.append('profile_image', selectedFile);
            }

            // Add remove image flag if set
            if (removeImage) {
                formDataToSend.append('remove_image', 'true');
            }

            if (editingTestimonial) {
                await axios.put(`${API_URL}/api/testimonials/${editingTestimonial.id}`, formDataToSend, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                toast.success('Testimonial updated successfully');
            } else {
                await axios.post(`${API_URL}/api/testimonials`, formDataToSend, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                toast.success('Testimonial created successfully');
            }

            setShowForm(false);
            setEditingTestimonial(null);
            resetForm();
            setSelectedFile(null);
            setPreviewImage(null);
            setRemoveImage(false);
            fetchTestimonials();
        } catch (error) {
            console.error('Error saving testimonial:', error);
            toast.error(error.response?.data?.message || 'Failed to save testimonial');
        }
    };

    const handleEdit = (testimonial) => {
        setEditingTestimonial(testimonial);
        setFormData({
            author_name: testimonial.author_name,
            author_title: testimonial.author_title,
            author_company: testimonial.author_company,
            rating: testimonial.rating,
            testimonial_text: testimonial.testimonial_text,
            is_active: testimonial.is_active === 1 || testimonial.is_active === true,
            display_order: testimonial.display_order
        });

        // Set preview image if exists
        if (testimonial.profile_image) {
            setPreviewImage(`${API_URL}/${testimonial.profile_image}`);
        } else {
            setPreviewImage(null);
        }

        setSelectedFile(null);
        setRemoveImage(false);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) {
            return;
        }

        try {
            await axios.delete(`${API_URL}/api/testimonials/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            toast.success('Testimonial deleted successfully');
            fetchTestimonials();
        } catch (error) {
            console.error('Error deleting testimonial:', error);
            toast.error('Failed to delete testimonial');
        }
    };

    const handleToggleActive = async (id) => {
        try {
            await axios.patch(`${API_URL}/api/testimonials/${id}/toggle`, {}, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            toast.success('Testimonial status updated');
            fetchTestimonials();
        } catch (error) {
            console.error('Error toggling testimonial status:', error);
            toast.error('Failed to update testimonial status');
        }
    };

    const resetForm = () => {
        setFormData({
            author_name: '',
            author_title: '',
            author_company: '',
            rating: 5,
            testimonial_text: '',
            is_active: false, // Default to inactive for new testimonials
            display_order: 0
        });
        setSelectedFile(null);
        setPreviewImage(null);
        setRemoveImage(false);
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingTestimonial(null);
        resetForm();
    };

    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, i) => (
            <i
                key={i}
                className={`fas fa-star ${i < rating ? 'text-warning' : 'text-muted'}`}
            ></i>
        ));
    };

    if (loading) {
        return (
            <div className="container-fluid mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            <PageTitle
                icon="fa-star"
                title="Manage Testimonials"
                className="mb-4"
            />

            {/* Stats Cards */}
            <div className="row mb-4">
                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-primary shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                        Total Testimonials
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                                        {stats.total_testimonials || 0}
                                    </div>
                                </div>
                                <div className="col-auto">
                                    <i className="fas fa-star fa-2x text-gray-300"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-success shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                        Active Testimonials
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                                        {stats.active_testimonials || 0}
                                    </div>
                                </div>
                                <div className="col-auto">
                                    <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-info shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                        Average Rating
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                                        {stats.average_rating ? parseFloat(stats.average_rating).toFixed(1) : '0.0'}/5
                                    </div>
                                </div>
                                <div className="col-auto">
                                    <i className="fas fa-star-half-alt fa-2x text-gray-300"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-xl-3 col-md-6 mb-4">
                    <div className="card border-left-warning shadow h-100 py-2">
                        <div className="card-body">
                            <div className="row no-gutters align-items-center">
                                <div className="col mr-2">
                                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                        5-Star Reviews
                                    </div>
                                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                                        {stats.five_star_count || 0}
                                    </div>
                                </div>
                                <div className="col-auto">
                                    <i className="fas fa-star fa-2x text-gray-300"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add New Testimonial Button */}
            <div className="row mb-4">
                <div className="col-12">
                    <button
                        className="btn btn-primary"
                        onClick={() => setShowForm(true)}
                    >
                        <i className="fas fa-plus me-2"></i>
                        Add New Testimonial
                    </button>
                </div>
            </div>

            {/* Testimonial Form */}
            {showForm && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="card shadow">
                            <div className="card-header">
                                <h5 className="mb-0">
                                    {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                                </h5>
                                {!editingTestimonial && (
                                    <small className="text-muted">
                                        New testimonials are inactive by default. Set to active to display on landing page.
                                    </small>
                                )}
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Author Name *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="author_name"
                                                value={formData.author_name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Author Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="author_title"
                                                value={formData.author_title}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Company/Organization *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="author_company"
                                                value={formData.author_company}
                                                onChange={handleInputChange}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Rating *</label>
                                            <select
                                                className="form-select"
                                                name="rating"
                                                value={formData.rating}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="5">5 Stars</option>
                                                <option value="4">4 Stars</option>
                                                <option value="3">3 Stars</option>
                                                <option value="2">2 Stars</option>
                                                <option value="1">1 Star</option>
                                            </select>
                                        </div>
                                        <div className="col-md-3 mb-3">
                                            <label className="form-label">Display Order</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="display_order"
                                                value={formData.display_order}
                                                onChange={handleInputChange}
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Testimonial Text *</label>
                                        <textarea
                                            className="form-control"
                                            name="testimonial_text"
                                            value={formData.testimonial_text}
                                            onChange={handleInputChange}
                                            rows="4"
                                            required
                                        ></textarea>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Profile Image (Optional)</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/jpeg,image/jpg,image/png"
                                            onChange={handleFileChange}
                                        />
                                        <small className="text-muted">
                                            Allowed formats: JPG, JPEG, PNG. Max size: 5MB
                                        </small>
                                        {previewImage && (
                                            <div className="mt-2">
                                                <img
                                                    src={previewImage}
                                                    alt="Preview"
                                                    className="img-thumbnail"
                                                    style={{ maxWidth: '150px', maxHeight: '150px' }}
                                                />
                                                {editingTestimonial && (
                                                    <div className="mt-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => {
                                                                setRemoveImage(true);
                                                                setPreviewImage(null);
                                                                setSelectedFile(null);
                                                            }}
                                                        >
                                                            <i className="fas fa-trash me-1"></i>
                                                            Remove Profile Image
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            name="is_active"
                                            value={formData.is_active ? '1' : '0'}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                is_active: e.target.value === '1'
                                            }))}
                                        >
                                            <option value="1">Active (visible on landing page)</option>
                                            <option value="0">Inactive (hidden from landing page)</option>
                                        </select>
                                        <small className="text-muted">
                                            Active testimonials will be displayed on the landing page
                                        </small>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button type="submit" className="btn btn-primary">
                                            {editingTestimonial ? 'Update' : 'Create'} Testimonial
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handleCancel}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Testimonials List */}
            <div className="row">
                <div className="col-12">
                    <div className="card shadow">
                        <div className="superadmin-section">
                            <h6 className="m-0 font-weight-bold">All Testimonials</h6>
                        </div>
                        <div className="card-body">
                            {testimonials.length === 0 ? (
                                <div className="text-center py-4">
                                    <p className="text-muted">No testimonials found</p>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowForm(true)}
                                    >
                                        Add Your First Testimonial
                                    </button>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Order</th>
                                                <th>Profile</th>
                                                <th>Author</th>
                                                <th>Company</th>
                                                <th>Rating</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {testimonials.map((testimonial) => (
                                                <tr key={testimonial.id}>
                                                    <td>{testimonial.display_order}</td>
                                                    <td>
                                                        {testimonial.profile_image ? (
                                                            <img
                                                                src={`${API_URL}/${testimonial.profile_image}`}
                                                                alt={testimonial.author_name}
                                                                className="rounded-circle"
                                                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <div
                                                                className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                                                style={{
                                                                    width: '40px',
                                                                    height: '40px',
                                                                    backgroundColor: '#6c757d',
                                                                    fontSize: '16px',
                                                                    fontWeight: 'bold'
                                                                }}
                                                            >
                                                                {testimonial.author_name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <strong>{testimonial.author_name}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {testimonial.author_title}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td>{testimonial.author_company}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            {renderStars(testimonial.rating)}
                                                            <span className="ms-2">
                                                                ({testimonial.rating}/5)
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${testimonial.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                            {testimonial.is_active ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {new Date(testimonial.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td>
                                                        <div className="btn-group" role="group">
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => handleEdit(testimonial)}
                                                                title="Edit"
                                                            >
                                                                <i className="fas fa-edit"></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-warning"
                                                                onClick={() => handleToggleActive(testimonial.id)}
                                                                title={testimonial.is_active ? 'Deactivate' : 'Activate'}
                                                            >
                                                                <i className={`fas fa-${testimonial.is_active ? 'eye-slash' : 'eye'}`}></i>
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDelete(testimonial.id)}
                                                                title="Delete"
                                                            >
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Testimonials; 